import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listenToProviderBookings } from '../services/bookingService';
import JobRequestCard from '../components/JobRequestCard';
import './JobRequestsPage.css';

const JobRequestsPage = () => {
    const { currentUser, getProviderProfile } = useAuth();
    const [filter, setFilter] = useState('all');
    const [allJobs, setAllJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [providerProfile, setProviderProfile] = useState(null);

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        let unsubscribe = null;

        const setup = async () => {
            try {
                // Get provider profile to know their service category
                const profile = await getProviderProfile(currentUser.uid);
                setProviderProfile(profile);

                const serviceCategory = profile?.serviceCategory || 'electrician';

                // Listen to real-time bookings
                unsubscribe = listenToProviderBookings(
                    currentUser.uid,
                    serviceCategory,
                    (bookings) => {
                        // Guard against stale pending cards that are already assigned to someone else.
                        const visibleBookings = bookings.filter((booking) => {
                            if (booking.status !== 'pending') return true;
                            if (!booking.providerId) return true;
                            return booking.providerId === currentUser.uid;
                        });

                        setAllJobs(visibleBookings);
                        setLoading(false);
                    }
                );
            } catch (err) {
                console.error('Failed to setup job requests:', err);
                setLoading(false);
            }
        };

        setup();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [currentUser, getProviderProfile]);

    const filteredJobs = filter === 'all'
        ? allJobs
        : allJobs.filter(job => job.status === filter);

    const handleJobUpdate = () => {
        // Real-time listener will auto-update the list
    };

    return (
        <div className="job-requests-page">
            <div className="container">
                <div className="page-header">
                    <h1>Job Requests</h1>
                    <p>Manage your incoming and active job requests</p>
                    {providerProfile && (
                        <span className="provider-category-badge">
                            {providerProfile.serviceCategory}
                        </span>
                    )}
                </div>

                <div className="filter-section">
                    <button
                        className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All ({allJobs.length})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                        onClick={() => setFilter('pending')}
                    >
                        Pending ({allJobs.filter(j => j.status === 'pending').length})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'accepted' ? 'active' : ''}`}
                        onClick={() => setFilter('accepted')}
                    >
                        Accepted ({allJobs.filter(j => j.status === 'accepted').length})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'in-progress' ? 'active' : ''}`}
                        onClick={() => setFilter('in-progress')}
                    >
                        In Progress ({allJobs.filter(j => j.status === 'in-progress').length})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'work-completed' ? 'active' : ''}`}
                        onClick={() => setFilter('work-completed')}
                    >
                        Completed ({allJobs.filter(j => j.status === 'work-completed').length})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'payment-done' ? 'active' : ''}`}
                        onClick={() => setFilter('payment-done')}
                    >
                        Paid ({allJobs.filter(j => j.status === 'payment-done').length})
                    </button>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading job requests...</p>
                    </div>
                ) : (
                    <div className="jobs-list">
                        {filteredJobs.length > 0 ? (
                            filteredJobs.map((job) => (
                                <JobRequestCard
                                    key={job.id}
                                    job={job}
                                    onUpdate={handleJobUpdate}
                                />
                            ))
                        ) : (
                            <div className="empty-state">
                                <span className="empty-icon">📭</span>
                                <h3>No jobs found</h3>
                                <p>
                                    {filter === 'all'
                                        ? 'No job requests yet. New customer bookings will appear here in real-time!'
                                        : `There are no ${filter} jobs at the moment.`
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobRequestsPage;
